/* eslint-disable react/display-name */
/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2025-04-14 16:30:31
 */
import './index.scss'
import FilterControls from '../FilterControls'
import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle
} from 'react'
import { Collapse, Table, Select, Tooltip, Spin, message, Empty } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  getWatermarkStat,
  getWatermarkStatItem,
  getWatermarkTitle
} from '../../../../actions/dataStatistics'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
const { Panel } = Collapse
const { Option } = Select

// 水印项类型
interface WatermarkItem {
  key: string
  watermarkItemTitle: string
  watermarkItemContent: string
  watermarkCnt: number
}

// 面板数据类型
interface PanelData {
  key: string
  header: string
  photoCount: number
  isOpen: boolean
  items: WatermarkItem[]
}

// 选项类型
interface SupervisionOption {
  id: string
  title: string
}

// 过滤器类型
interface FilterData {
  filter?: {
    departments?: unknown[]
    departmentUsers?: unknown[]
    userIDs?: string[]
    times?: {
      start: string
      end: string
    }
    [key: string]: unknown
  }
  [key: string]: unknown
}

// Props 类型
interface WatermarkStatProps {
  handleClickReportSA: (
    clickItem: string,
    params?: Record<string, unknown>
  ) => void
}

// 暴露的 ref 方法类型
export interface WatermarkStatRef {
  refreshData: () => Promise<{ success: boolean; error?: unknown }>
}

const WatermarkStat = forwardRef<WatermarkStatRef, WatermarkStatProps>(
  ({ handleClickReportSA }, ref) => {
    // 使用 hooks 替代 connect
    const pickerUserCount = useAppSelector(
      (state) => state.organization.pickerOrgUserCount
    )
    const dispatch = useAppDispatch()
    const [panelData, setPanelData] = useState<PanelData[]>([])
    const [activeKeys, setActiveKeys] = useState<string[]>([])
    const [loading, setLoading] = useState(false)
    const [selectedOption, setSelectedOption] = useState<string | undefined>(
      undefined
    )
    const [currentFilter, setCurrentFilter] = useState<FilterData | null>(null)
    const [supervisionOptions, setSupervisionOptions] = useState<
      SupervisionOption[]
    >([])

    useEffect(() => {
      // 初始化逻辑
      const initialActiveKeys = panelData
        .filter((panel) => panel.isOpen)
        .map((panel) => panel.key)
      setActiveKeys(initialActiveKeys)
    }, [panelData])

    // 从过滤器中移除部门相关数据
    const removeDeparmentsFromFilter = (filter: FilterData) => {
      const { departments, departmentUsers, ...rest } = filter.filter || {}
      return {
        ...filter,
        filter: rest
      }
    }

    const fetchWatermarkData = async (filter: FilterData | null) => {
      if (!filter) return { success: false }
      setLoading(true)
      try {
        // 获取水印条目列表
        const cleanFilter = removeDeparmentsFromFilter(filter)
        const res = await dispatch(getWatermarkStat(cleanFilter))

        const formattedData = res.watermarks.map(
          ({
            watermarkBaseID,
            groupWatermarkID,
            groupWatermarkName,
            watermarkCnt
          }) => ({
            key: `${watermarkBaseID}+${groupWatermarkID}`,
            header: groupWatermarkName,
            photoCount: watermarkCnt,
            isOpen: false,
            items: []
          })
        )
        setPanelData(formattedData)

        // 获取下拉框选项
        const optionsRes = await dispatch(getWatermarkTitle(cleanFilter))
        optionsRes.items = optionsRes.items.map((item) => item.title)
        const options = optionsRes.items.map((item) => ({
          id: item,
          title: item
        }))
        setSupervisionOptions(options)

        return { success: true }
      } catch (error) {
        return { success: false, error }
      } finally {
        setLoading(false)
      }
    }

    // 过滤条件改变时触发
    const handleFilterChange = async (newFilter: FilterData) => {
      setCurrentFilter(newFilter)
      setSelectedOption(undefined) // 清空下拉框的选择内容

      return fetchWatermarkData(newFilter)
    }

    // 折叠面板展开或关闭时触发
    const handlePanelChange = async (keys: string | string[]) => {
      const keysArray = Array.isArray(keys) ? keys : [keys]
      if (!selectedOption) {
        message.info('请选择条目')
        return
      }

      // 找出变化的面板key
      const currentKey =
        keysArray.find((key) => !activeKeys.includes(key)) ||
        activeKeys.find((key) => !keysArray.includes(key))

      const currentPanel = panelData.find((p) => p.key === currentKey)
      setActiveKeys(keysArray)

      // 更新面板打开状态
      setPanelData((prevPanelData) =>
        prevPanelData.map((panel) => ({
          ...panel,
          isOpen: panel.key === currentKey ? !panel.isOpen : panel.isOpen
        }))
      )

      // 如果折叠面板展开且items为空，获取远程数据
      const panel = panelData.find((p) => p.key === currentKey)
      if (!panel) return
      // 上报埋点
      if (!panel.isOpen) {
        handleWatermarkStatClickReportSA('expandWatermarkName', {
          watermarkName: panel.header
        })
      } else {
        handleWatermarkStatClickReportSA('retractWatermarkName', {
          watermarkName: panel.header
        })
      }
      if (!panel.isOpen && panel.items.length === 0) {
        setLoading(true)
        try {
          if (!currentKey || !currentFilter) return
          const [watermarkBaseID, groupWatermarkID = ''] = currentKey.split('+')

          // 准备请求参数
          const { departments, departmentUsers, ...restFilter } =
            currentFilter.filter || {}
          const filter = {
            ...currentFilter,
            filter: {
              ...restFilter,
              watermarks: [{ watermarkBaseID, groupWatermarkID }],
              groupWatermarkItemTitle: selectedOption
            }
          }

          const res = await dispatch(getWatermarkStatItem(filter))

          // 处理响应数据
          if (res.watermarks[currentKey.split('+')[1]]) {
            const items = res.watermarks[currentKey.split('+')[1]].map(
              ({
                watermarkItemContent,
                watermarkCnt
              }: {
                watermarkItemContent: string
                watermarkCnt: number
              }) => ({
                key: currentPanel?.key || '',
                watermarkItemTitle: currentPanel?.header || '',
                watermarkItemContent,
                watermarkCnt
              })
            )
            // 更新面板数据
            setPanelData((prevData) =>
              prevData.map((p) => (p.key === currentKey ? { ...p, items } : p))
            )
          }
        } finally {
          setLoading(false)
        }
      }
    }

    // 选择监理内容时触发
    const handleSelectChange = async (value: string) => {
      setSelectedOption(value)
      // 上报埋点
      const index = supervisionOptions.findIndex(
        (option) => option.id === value
      )
      handleWatermarkStatClickReportSA('chooseItemContent', {
        clickContent: value,
        itemRank: index + 1
      })

      // 获取折叠面板里面展开的数据
      const activePanel = panelData.filter((p) => p.isOpen)
      if (activePanel.length === 0 && panelData.length > 0) {
        // 如果没有展开的面板且有面板数据，自动展开第一个面板
        const firstPanelKey = panelData[0].key
        setActiveKeys([firstPanelKey])

        // 更新面板状态，将第一个面板标记为打开
        setPanelData((prevData) =>
          prevData.map((p, idx) => ({
            ...p,
            isOpen: idx === 0, // 只将第一个面板设为打开状态
            items: p.items // 保留原有的items，不需要清空
          }))
        )

        // 上报埋点
        handleWatermarkStatClickReportSA('expandWatermarkName', {
          watermarkName: panelData[0].header
        })
      }

      // 清空所有面板的items
      setPanelData((prevData) => prevData.map((p) => ({ ...p, items: [] })))

      // 重新获取已展开面板的数据
      // 注意：这里需要使用更新后的activePanel
      const updatedActivePanel =
        panelData.length > 0 && activePanel.length === 0
          ? [{ ...panelData[0], isOpen: true }] // 如果刚才展开了第一个面板，使用它
          : activePanel

      if (updatedActivePanel.length === 0) {
        return // 如果没有展开的面板，提前返回
      }

      // 获取watermark - 从已展开的面板中获取
      const watermark = updatedActivePanel.map(({ key }) => {
        const [watermarkBaseID, groupWatermarkID] = key.split('+')
        return { watermarkBaseID, groupWatermarkID }
      })

      // 准备请求参数
      if (!currentFilter) return
      const {
        departments: _d,
        departmentUsers: _du,
        ...restFilter2
      } = currentFilter.filter || {}
      const filter = {
        ...currentFilter,
        filter: {
          ...restFilter2,
          watermarks: watermark,
          groupWatermarkItemTitle: value
        }
      }

      setLoading(true)
      try {
        const res = await dispatch(getWatermarkStatItem(filter))

        // 更新每个活动面板的数据
        const activePanelKeys = updatedActivePanel.map((p) => p.key)
        activePanelKeys.forEach((key) => {
          const data = res.watermarks[key.split('+')[1]]
          const currentPanel = panelData.find((p) => p.key === key)
          const currentPanelHeader = currentPanel?.header || ''

          if (data) {
            const items = data.map((item: WatermarkItem, index: number) => ({
              ...item,
              watermarkItemTitle: currentPanelHeader,
              key: `${currentPanel?.key}_${index}`
            }))

            setPanelData((prevData) =>
              prevData.map((p) => (p.key === key ? { ...p, items } : p))
            )
          }
        })
      } finally {
        setLoading(false)
      }
    }

    // 点击拍摄数量时触发
    const handlePhotoCountClick = (record: WatermarkItem) => {
      const { key, watermarkItemContent, watermarkCnt, watermarkItemTitle } =
        record
      handleWatermarkStatClickReportSA('watermarkManagePhotoNum', {
        number3: watermarkCnt,
        watermarkName: watermarkItemTitle,
        itemContent: selectedOption
      })
      jumpToPhotoList(key.split('_')[0], watermarkItemContent)
    }

    const handlePanelPhotoCountClick = (panel: PanelData) => {
      handleWatermarkStatClickReportSA('watermarkManagePhotoNum', {
        number3: panel.photoCount,
        watermarkName: panel.header,
        itemContent: selectedOption || ''
      })
      jumpToPhotoList(panel.key)
    }

    // 跳转至照片列表
    const jumpToPhotoList = (
      watermarkItemID: string,
      watermarkItemContent?: string
    ) => {
      // 设置session的departments和departmentUsers
      const departments = currentFilter?.filter?.departments || []
      const departmentUsers = currentFilter?.filter?.departmentUsers || []
      sessionStorage.setItem('departments', JSON.stringify(departments))
      sessionStorage.setItem('departmentUsers', JSON.stringify(departmentUsers))
      const { userIDs: departmentIDList = [], times } =
        currentFilter?.filter || {}
      const { start, end } = times || {}
      let queryParams = ''

      if (departmentIDList.length !== 0) {
        queryParams += `department_id=${encodeURIComponent(
          departmentIDList.join(',')
        )}&`
      }
      if (start && end) {
        queryParams += `start_time=${encodeURIComponent(
          start
        )}&end_time=${encodeURIComponent(end)}&`
      }
      if (watermarkItemContent) {
        queryParams += `filter_item_content=${encodeURIComponent(
          watermarkItemContent
        )}&`
      }

      // 移除末尾的 '&' 符号
      queryParams = queryParams.slice(0, -1)
      sessionStorage.setItem('watermarks', JSON.stringify([watermarkItemID]))
      window.open(
        `/photo/all?form_place=data_statistics_watermark&has_member=true&has_watermark=true&${queryParams}`
      )
    }

    const columns: ColumnsType<WatermarkItem> = [
      {
        title: '水印名称',
        dataIndex: 'watermarkItemTitle',
        key: 'watermarkItemTitle',
        width: '28%'
      },
      {
        title: '监理内容',
        dataIndex: 'watermarkItemContent',
        key: 'watermarkItemContent',
        width: '57%',
        render: (text: string) => {
          return text && text.length > 0 ? text : '-'
        }
      },
      {
        title: '拍摄数量',
        dataIndex: 'watermarkCnt',
        key: 'watermarkCnt',
        width: '16%',
        render: (text: number, record: WatermarkItem) => (
          <span
            style={{ color: '#407AFF', cursor: 'pointer' }}
            onClick={() => handlePhotoCountClick(record)}
          >
            {text}
          </span>
        )
      }
    ]

    const handleWatermarkStatClickReportSA = (
      clickItem: string,
      params?: Record<string, unknown>
    ) => {
      handleClickReportSA(clickItem, {
        themeType: 'watermarkManage',
        ...params
      })
    }

    // 暴露刷新方法
    useImperativeHandle(ref, () => ({
      refreshData: async () => {
        setSelectedOption(undefined) // 清空下拉框的选择内容
        return fetchWatermarkData(currentFilter)
      }
    }))

    return (
      <div
        className="main-content-container watermark-stat-container"
        style={{ padding: '0', minHeight: '400px' }}
      >
        <Spin spinning={loading}>
          <FilterControls
            pickerUserCount={pickerUserCount}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onFilterChange={handleFilterChange as any}
            handleClickReportSA={handleWatermarkStatClickReportSA}
            title="水印统计"
          />

          {panelData.length > 0 ? (
            // 有数据时显示正常内容
            <>
              <div
                style={{
                  display: 'flex',
                  padding: '10px 20px',
                  alignItems: 'center'
                }}
                className="watermark-stat-header"
              >
                <span style={{ flex: 2 }}>水印名称</span>
                <div
                  style={{
                    flex: 4,
                    border: 'none',
                    boxShadow: 'none',
                    paddingRight: '10px'
                  }}
                >
                  <Select
                    value={selectedOption}
                    bordered={false}
                    optionLabelProp="label"
                    onChange={handleSelectChange}
                    placeholder="条目（未选择）"
                    style={{ minWidth: '160px' }}
                    onPopupScroll={() => {
                      handleWatermarkStatClickReportSA('slideItemList')
                    }}
                    onDropdownVisibleChange={(open) => {
                      // 当下拉菜单打开时触发
                      if (open) {
                        handleWatermarkStatClickReportSA('itemListShow')
                      }
                      handleWatermarkStatClickReportSA('chooseSecondColumn')
                    }}
                  >
                    {supervisionOptions.map((option) => (
                      <Option
                        key={option.id}
                        value={option.id}
                        label={option.title}
                      >
                        <Tooltip title={option.title}>
                          <span>{option.title}</span>
                        </Tooltip>
                      </Option>
                    ))}
                  </Select>
                </div>
                <span style={{ flex: 1 }}>拍摄数量</span>
              </div>
              <Collapse activeKey={activeKeys} onChange={handlePanelChange}>
                {panelData.map((panel) => (
                  <Panel
                    header={
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between'
                        }}
                      >
                        <span style={{ flex: 2 }}>{panel.header}</span>
                        <span style={{ flex: 4 }}></span>
                        <span
                          style={{
                            flex: 1,
                            color: '#407AFF',
                            cursor: 'pointer'
                          }}
                          onClick={(e) => {
                            e.stopPropagation()
                            handlePanelPhotoCountClick(panel)
                          }}
                        >
                          {panel.photoCount}
                        </span>
                      </div>
                    }
                    key={panel.key}
                  >
                    <Table
                      columns={columns}
                      dataSource={panel.items}
                      pagination={false}
                      rowKey="key"
                      showHeader={false}
                    />
                  </Panel>
                ))}
              </Collapse>
            </>
          ) : (
            // 无数据时显示空状态
            <div
              style={{
                width: '100%',
                height: '400px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <Empty description="暂无数据" />
            </div>
          )}
        </Spin>
      </div>
    )
  }
)

export default WatermarkStat
